import { gql } from '@apollo/client';

// ===================================================================
// Fragments
// ===================================================================

export const SFTP_SETUP_LINK_FRAGMENT = gql`
  fragment SftpSetupLinkFields on SftpSetupLink {
    id
    mode
    recipientEmail
    initiatedByEmail
    expiresAt
    createdAt
  }
`;

// ===================================================================
// Queries
// ===================================================================

export const GET_SFTP_ACCESS = gql`
  ${SFTP_SETUP_LINK_FRAGMENT}
  query GetSftpAccess($organizationId: ID!) {
    sftpAccess(organizationId: $organizationId) {
      configured
      user {
        id
        username
        homeDirectory
        createdAt
      }
      keyState
      currentKeyImportedAt
      previousKeyGraceExpiresAt
      host
      port
      pendingLink {
        ...SftpSetupLinkFields
      }
      lastClaim {
        claimedAt
        fromIp
        fromCountry
        recipientEmail
      }
    }
  }
`;

// ===================================================================
// Mutations
//
// None of these return key material — see server/src/graphql/schema/sftpAccess.graphql.
// The private key is only ever produced when the client redeems their emailed
// link, and is returned once by REST.
// ===================================================================

export const GENERATE_SFTP_ACCESS = gql`
  ${SFTP_SETUP_LINK_FRAGMENT}
  mutation GenerateSftpAccess($organizationId: ID!, $recipientEmail: String!) {
    generateSftpAccess(organizationId: $organizationId, recipientEmail: $recipientEmail) {
      ...SftpSetupLinkFields
    }
  }
`;

export const ROLL_SFTP_KEY = gql`
  ${SFTP_SETUP_LINK_FRAGMENT}
  mutation RollSftpKey($organizationId: ID!, $recipientEmail: String!) {
    rollSftpKey(organizationId: $organizationId, recipientEmail: $recipientEmail) {
      ...SftpSetupLinkFields
    }
  }
`;

export const RESEND_SFTP_SETUP_LINK = gql`
  ${SFTP_SETUP_LINK_FRAGMENT}
  mutation ResendSftpSetupLink($setupLinkId: ID!, $recipientEmail: String) {
    resendSftpSetupLink(setupLinkId: $setupLinkId, recipientEmail: $recipientEmail) {
      ...SftpSetupLinkFields
    }
  }
`;

export const CANCEL_SFTP_SETUP_LINK = gql`
  mutation CancelSftpSetupLink($setupLinkId: ID!) {
    cancelSftpSetupLink(setupLinkId: $setupLinkId)
  }
`;

export const REVOKE_PREVIOUS_SFTP_KEY = gql`
  mutation RevokePreviousSftpKey($organizationId: ID!) {
    revokePreviousSftpKey(organizationId: $organizationId) {
      revoked
      revokedKeyCount
    }
  }
`;

/**
 * Ends SFTP access entirely -- distinct from REVOKE_PREVIOUS_SFTP_KEY, which
 * only closes a rotation's grace window and leaves the client working.
 */
export const REVOKE_SFTP_ACCESS = gql`
  mutation RevokeSftpAccess($organizationId: ID!) {
    revokeSftpAccess(organizationId: $organizationId) {
      revoked
      username
    }
  }
`;

// ===================================================================
// TypeScript Types
// ===================================================================

export type SftpKeyState = 'PENDING_KEY' | 'ACTIVE' | 'ROTATING';
export type SftpSetupLinkMode = 'CREATE' | 'ROTATE';

export interface SftpSetupLink {
  id: string;
  mode: SftpSetupLinkMode;
  recipientEmail: string;
  initiatedByEmail: string;
  expiresAt: string;
  createdAt: string;
}

export interface SftpUser {
  id: string;
  username: string;
  homeDirectory: string;
  createdAt: string;
}

export interface SftpKeyClaim {
  claimedAt: string;
  fromIp: string | null;
  fromCountry: string | null;
  recipientEmail: string;
}

export interface SftpAccess {
  configured: boolean;
  user: SftpUser | null;
  keyState: SftpKeyState | null;
  currentKeyImportedAt: string | null;
  previousKeyGraceExpiresAt: string | null;
  host: string | null;
  port: number | null;
  pendingLink: SftpSetupLink | null;
  lastClaim: SftpKeyClaim | null;
}

export interface GetSftpAccessData {
  sftpAccess: SftpAccess;
}

export interface GetSftpAccessVars {
  organizationId: string;
}

export interface GenerateSftpAccessVars {
  organizationId: string;
  recipientEmail: string;
}

export interface RollSftpKeyVars {
  organizationId: string;
  recipientEmail: string;
}

export interface ResendSftpSetupLinkVars {
  setupLinkId: string;
  recipientEmail?: string;
}

export interface CancelSftpSetupLinkVars {
  setupLinkId: string;
}

export interface RevokePreviousSftpKeyVars {
  organizationId: string;
}

export interface RevokePreviousSftpKeyData {
  revokePreviousSftpKey: {
    revoked: boolean;
    revokedKeyCount: number;
  };
}

export interface RevokeSftpAccessVars {
  organizationId: string;
}

export interface RevokeSftpAccessData {
  revokeSftpAccess: {
    revoked: boolean;
    username: string | null;
  };
}
